import { StorageService } from "./storage.service";
import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { Observable, throwError } from "rxjs";
import { timeout, tap } from 'rxjs/operators';
import { PayloadIA, quoteHeadItemComp } from "../models/models";

@Injectable({
  providedIn: "root"
})
export class ApiServiceService {
  private apiBaseUrl = "";
  private apiUrl = "";

  constructor(
    private http: HttpClient,
    private storageService: StorageService
  ) {
    this.apiBaseUrl = environment["API_BASE_URL"] || process.env.API_BASE_URL;
    this.apiUrl = this.apiBaseUrl+'crm';
  }

  // File management methods for events
  getFileUploadUrl(): string {
    return `${this.apiBaseUrl}files/upload/save`;
  }

  getEventDocuments(eventKey: number): Observable<any> {
    const token = this.storageService.getAccessToken();
    if (!token) {
      console.error('getEventDocuments: No token available');
      return throwError(() => new Error('Authentication required'));
    }
    const data = {
      eventKey: eventKey,
      folderCode: 'CRMEV'  // Add required folderCode parameter
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiBaseUrl}files/crm/event/list`, data, { headers });
  }

  downloadDocument(allegatiListKey: string): Observable<any> {
    const token = this.storageService.getAccessToken();
    if (!token) {
      console.error('downloadDocument: No token available');
      return throwError(() => new Error('Authentication required'));
    }
    const data = {
      allegatiListKey: allegatiListKey
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiBaseUrl}files/get`, data, { 
      headers,
      responseType: 'blob',
      observe: 'response'
    });
  }

  deleteDocument(keyFile: string): Observable<any> {
    const token = this.storageService.getAccessToken();
    if (!token) {
      console.error('deleteDocument: No token available');
      return throwError(() => new Error('Authentication required'));
    }
    const data = {
      keyFile: keyFile
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiBaseUrl}files/delete`, data, { headers });
  }

  uploadEventDocuments(eventKey: number, files: File[]): Observable<any> {
    const token = this.storageService.getAccessToken();
    if (!token) {
      console.error('uploadEventDocuments: No token available');
      return throwError(() => new Error('Authentication required'));
    }

    const formData = new FormData();
    formData.append('eventKey', eventKey.toString());
    formData.append('accessToken', token);
    
    // Aggiungi tutti i file al FormData
    files.forEach((file, index) => {
      formData.append(`file_${index}`, file, file.name);
    });

    // Non impostare Content-Type per FormData, il browser lo gestisce automaticamente
    return this.http.post(`${this.apiBaseUrl}files/crm/event/upload`, formData);
  }

  login(user, password) {
    const data = { username: user, password: password };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/login`, data, { headers });
  }

  recoverCredentials(username: string, email: string) {
    const data = { username, email };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/recover-credentials`, data, { headers });
  }

  listCustomers(
    userId,
    isAdmin,
    head_branches,
    page,
    pageSize,
    querySearch,
    customerId,
    branch: string | string[] = undefined,
    localita = undefined,
    filterTypeCustomer = undefined,
    isExcelFile = false,
    contactGroupIds = undefined,
    office = undefined,  // 🏢 Office filter
    yearCreation = undefined,
    yearSalesAssignment = undefined,
    sortColumn = undefined,
    sortDirection = undefined,
    filterConflicts: 'yes' | 'no' | undefined = undefined,  // 🔀 Cross-installation conflict
    salesIds: (number | string)[] = undefined,  // 👤 Multi-select Sales
    includeVolumes: boolean = false,  // 📊 Export Excel: 3 colonne volumi (TEU/KG/Colli). Richiede EXPORT_CUSTOMERS_EXCEL_VOLUMES (validato lato BE).
    expiryStatus: 'expiring' | 'expired' | undefined = undefined,  // ⏳ Prospect in scadenza
    expiryDays: number = 30  // ⏳ Soglia giorni "in scadenza" (scelta utente)
  ) {

    const data = {
      idtab: this.storageService.getIdTab("cliana"),
      page: page,
      pageSize: pageSize,
      search: querySearch,
      id: customerId,
      userId: userId,
      isAdmin: isAdmin,
      // 🔁 Retrocompat: backend accetta string|string[]|CSV. Serializzo array → CSV.
      branch: Array.isArray(branch) ? branch.filter(Boolean).join(',') : branch,
      locality: localita,
      contactGroupIds: contactGroupIds,
      head_branches: head_branches,
      filterTypeCustomer: filterTypeCustomer,
      default_installation: this.storageService.getUserStorage()?.default_installation,
      default_branches:  this.storageService.getUserStorage()?.branch_key?.toString(),
      isExcelFile: isExcelFile,
      isAllNoJobs: this._isAllNoJobs(),
      office: office,  // 🏢 Office filter
      yearCreation: yearCreation,
      yearSalesAssignment: yearSalesAssignment,
      sortColumn: sortColumn,
      sortDirection: sortDirection,
      filterConflicts: filterConflicts,  // 🔀 Cross-installation conflict
      salesIds: Array.isArray(salesIds) ? salesIds.filter(v => v !== null && v !== undefined && v !== '') : salesIds,  // 👤 Sales filter
      includeVolumes: includeVolumes,  // 📊 Volumi 12m per installazione (solo export Excel)
      expiryStatus: expiryStatus,  // ⏳ Prospect in scadenza: 'expiring' | 'expired' | undefined
      expiryDays: expiryDays  // ⏳ Soglia giorni "in scadenza"
    };
    
    // If Excel export, we need to handle the response as a blob
    if (isExcelFile) {
      return this.http.post(`${this.apiUrl}/customers/list`, data, {
        responseType: 'blob',
        observe: 'response'
      });
    }

    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/list`, data, { headers });
  }

  /**
   * ⏳ Conteggi prospect in scadenza/scaduti per i pill di riepilogo della lista clienti.
   * Lo scope (proprie vs tutte) è deciso dal backend in base a flgUserType.
   * Ritorna { code, result: { expiringSoon, expired } }.
   */
  getProspectExpirySummary(userId: number, flgUserType: string, expiryDays: number = 30) {
    const data = {
      userKey: userId,
      flgUserType: flgUserType,
      default_installation: this.storageService.getUserStorage()?.default_installation,
      expiryDays: expiryDays  // ⏳ Soglia giorni "in scadenza"
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/expiry/summary`, data, { headers });
  }

  getCrossInstallationConflicts(customerIds: number[]): Observable<any> {
    const data = { customerIds };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/cross-installation`, data, { headers });
  }

  getSalesAssignmentYears(): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/sales/assignment-years`, {}, { headers });
  }

  searchLocalities(search: string) {
    const data = {
      search: search,
      default_installation: this.storageService.getUserStorage()?.default_installation
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/localities`, data, { headers });
  }

  searchContactGroups(search: string) {
    const user = this.storageService.getUserStorage();
    const data = {
      search: search,
      userId: user?.id,
      flgUserType: user?.flgUserType,
      head_branches: user?.head_branches
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/contact_groups/search`, data, { headers });
  }

  listCustomersUser(userId, page, pageSize, querySearch, customerId) {
    const data = {
      idtab: undefined,
      page: page,
      pageSize: pageSize,
      search: querySearch,
      id: customerId,
      userId: userId,
      isAdmin: false,
      onlyUserCustomer: true,
      // Era 'events', lo stesso tipo filtro che usa la scheda evento. Due schermate
      // che fanno domande diverse — "di chi e' questo cliente" e "su chi posso
      // registrare una visita" — non possono condividere il filtro: cambiarlo per
      // una lo cambiava per l'altra.
      filterTypeCustomer: 'usercustomers',
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/list`, data, { headers });
  }

  loadCustomer(customerId) {
    const data = {
      idtab: undefined,
      page: 1,
      pageSize: 1,
      search: undefined,
      id: customerId,
      userId: undefined,
      isAdmin: false
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/list`, data, { headers });
  }

  listPaesi() {
    const idtab = this.storageService.getIdTab("paesi");
    const data = { idtab: idtab };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/list/paesi`, data, { headers });
  }

  listSettori() {
    // L'installazione va MANDATA: il catalogo settori si risolve dalla configurazione
    // (gengestab) e non è più la stringa 'DCS16arktab' scritta nel backend. Stessa
    // sorgente delle altre chiamate che già la mandano.
    const data = { default_installation: this.storageService.getUserStorage()?.default_installation };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/list/settori`, data, { headers });
  }

  listProvince(paesiKey: number) {
    const data = { paesiKey: paesiKey };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/list/province`, data, { headers });
  }

  saveCustomerObject(customerObject) {
    const data = {
      customer: customerObject,
      userId: this.storageService.getUserStorage()?.username,
      // L'installazione la decide il cliente che si sta salvando, non il profilo di
      // chi salva: con il selettore in maschera i due valori possono essere diversi,
      // e mandare quello del login significherebbe scrivere il record in un archivio
      // e controllarne un altro (duplicati P.IVA, regole DCN). Fallback al vecchio
      // comportamento se per qualunque motivo il cliente non porta l'idtab.
      idtab: customerObject?.cliana_idtab || this.storageService.getIdTab("cliana"),
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/save`, data, { headers });
  }

  createProspectInInstallation(sourceCustomerId: number, targetInstallationCode: string, salesUserKey: number, salesUsername: string, iec: string, tma: string) {
    const user = this.storageService.getUserStorage();
    const data = {
      sourceCustomerId,
      targetInstallationCode,
      salesUserKey,
      salesUsername,
      iec,
      tma,
      userKey: user?.id,
      userId: user?.username
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/createInInstallation`, data, { headers });
  }

  getProspectCountByUser(userKey: number): Observable<any> {
    const data = { userKey };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/prospect/count`, data, { headers });
  }

  /**
   * Invalidate a single prospect customer (requires INVALIDATE_PROSPECT right)
   * SECURITY: Only single customerId allowed, no bulk operations
   * @param customerId - Single customer ID (number, not array)
   */
  invalidateProspect(customerId: number) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    const user = this.storageService.getUserStorage();
    const userName = user?.username;
    const userKey = user?.id;
    return this.http.post(`${this.apiUrl}/customer/prospect/invalidate`, { customerId, userName, userKey }, { headers });
  }

  /**
   * Transform a prospect into a definitive customer (SuperAdmin only, no-jobs installations)
   * @param customerId - Single customer ID (number, not array)
   * @param codalt1 - External gestionale code (max 10 chars)
   */
  transformProspectToCustomer(customerId: number, codalt1: string) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    const userName = this.storageService.getUserStorage()?.username;
    return this.http.post(`${this.apiUrl}/customer/prospect/transform`, { customerId, userName, codalt1 }, { headers });
  }

  // ════════════════════════════════════════════════════════════
  // PROSPECT IMPORT — Sales Leads Import dashboard (md-plan/prospect-dcn)
  // ════════════════════════════════════════════════════════════

  /**
   * List prospect imports for the Sales Leads Import dashboard.
   * Date filters are applied server-side; the endpoint paginates in memory.
   */
  listProspectImports(filters: {
    channel?: string | null;
    status?: string | null;
    salesUserKey?: number | null;
    clianaKey?: number | null;
    dateFrom?: string | null;
    dateTo?: string | null;
    page?: number | null;
    pageSize?: number | null;
  }) {
    const data = {
      userKey: this.storageService.getUserStorage()?.id,
      channel: filters.channel ?? null,
      status: filters.status ?? null,
      salesUserKey: filters.salesUserKey ?? null,
      clianaKey: filters.clianaKey ?? null,
      dateFrom: filters.dateFrom ?? null,
      dateTo: filters.dateTo ?? null,
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? 50
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/prospect-import/list`, data, { headers });
  }

  /** Full detail of a prospect import: header + submissions history. */
  getProspectImportDetail(importKey: number) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/prospect-import/detail`, { importKey }, { headers });
  }

  /** Re-run the missing steps of a prospect import (pipeline or post-insert). */
  retryProspectImport(importKey: number) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/prospect-import/retry`, { importKey }, { headers });
  }

  /** Regenerate and download the Intelligence Brief PDF (on-request reprint). */
  downloadProspectBriefPdf(importKey: number) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/prospect-import/brief-pdf`, { importKey }, {
      headers,
      responseType: 'blob' as 'blob',
      observe: 'response' as 'response'
    });
  }

  /**
   * Search customers by VAT number for merge destination selection
   * @param vatNumber - VAT number to search
   * @param excludeClianaKey - Customer key to exclude (source prospect)
   * @param idtab - Installation idtab to filter by
   */
  searchCustomersByVat(vatNumber: string, excludeClianaKey: number, idtab: string) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/search-by-vat`, { vatNumber, excludeClianaKey, idtab }, { headers });
  }

  /**
   * Merge events from source prospect to target customer and invalidate source (BB user only)
   * Supports two modes:
   * 1. Merge + Invalidate: targetClianaKey is provided
   * 2. Invalidation Only: targetClianaKey is null
   * @param sourceClianaKey - Source prospect to invalidate
   * @param targetClianaKey - Target customer to receive events (null = invalidation only)
   * @param options - { transferEvents: boolean, transferContacts: boolean, transferSales: boolean }
   */
  mergeAndInvalidateProspect(sourceClianaKey: number, targetClianaKey: number | null, options: { transferEvents: boolean, transferContacts: boolean, transferSales: boolean }) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    const user = this.storageService.getUserStorage();
    const userName = user?.username;
    const userKey = user?.id;
    return this.http.post(`${this.apiUrl}/customer/prospect/merge-invalidate`, {
      sourceClianaKey,
      targetClianaKey,
      userName,
      userKey,
      options
    }, { headers });
  }

  /**
   * Count events for a customer (for merge preview)
   * @param clianaKey - Customer key
   */
  countCustomerEvents(clianaKey: number) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/events/list/table`, { 
      clianaKey, 
      bitVisible: 0,
      page: 1,
      pageSize: 1 
    }, { headers });
  }

  /**
   * Get contacts for a customer (for merge preview count)
   * @param clianaKey - Customer key
   */
  /**
   * Conteggio GLOBALE dei contatti del cliente (preview del merge, che sposta
   * tutti i contatti del sorgente). Torna solo il numero: i dati dei contatti
   * restano riservati a chi li ha inseriti e al SuperAdmin.
   */
  countCustomerContacts(clianaKey: number) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/contact/list`, { clianaKey, countOnly: true }, { headers });
  }

  /**
   * Count sales manager assignments for a customer (for merge preview)
   * @param clianaKey - Customer key
   */
  countCustomerSales(clianaKey: number) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/sales/count`, { clianaKey }, { headers });
  }

  listAllUsers() {
    const data = { idtab: this.storageService.getIdTab("cliana") };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/list/users/all`, data, { headers });
  }

  listUsersByInstallation(installationCode: string) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/list/users/byInstallation`, { installationCode }, { headers });
  }

  /**
   * Dashboard Vendite — aggregato per cliente sul periodo richiesto.
   * Stored: dcs_sp_apiInternal_dashboardSales_byCustomer
   *
   * @param filters.dateFrom   YYYY-MM-DD (required)
   * @param filters.dateTo     YYYY-MM-DD (required)
   * @param filters.instName   inst_name (es. 'DCS')   - opzionale
   * @param filters.officeKey  filiale_key             - opzionale
   * @param filters.salesKey   utenti_key              - opzionale
   * @param filters.clianaKey  singolo cliente         - opzionale
   */
  /**
   * Pratiche in ARRIVO nel settore/nazione dell'utente in sessione (project-guede).
   * Cross-installazione. Il backend risolve nazione/settore dalla default_installation
   * dell'utente; qui passiamo solo userKey + filtri (range date, IEC/TMA, mode override).
   */
  getArrivals(filters: {
    arrFrom?: string | null;
    arrTo?: string | null;
    depFrom?: string | null;
    depTo?: string | null;
    tma?: string[] | null;
    iec?: string[] | null;
    incoterms?: string[] | null;
    consigneeLike?: string | null;
    sectorKeys?: (string | number)[] | null;
    nationCodes?: string[] | null;
    page?: number | null;
    pageSize?: number | null;
  }) {
    const data = {
      userKey: this.storageService.getUserStorage()?.id,
      arrFrom: filters.arrFrom ?? null,
      arrTo:   filters.arrTo   ?? null,
      depFrom: filters.depFrom ?? null,
      depTo:   filters.depTo   ?? null,
      tma:     filters.tma     ?? null,
      iec:     filters.iec     ?? null,
      incoterms: filters.incoterms ?? null,
      consigneeLike: filters.consigneeLike ?? null,
      sectorKeys:  filters.sectorKeys  ?? null,
      nationCodes: filters.nationCodes ?? null,
      page:    filters.page    ?? 1,
      pageSize: filters.pageSize ?? 50
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/arrivals/list`, data, { headers });
  }

  /** Catalogo incoterms (resa) per il filtro a tendina multiplo della pagina arrivi. */
  getArrivalsIncoterms() {
    const data = { userKey: this.storageService.getUserStorage()?.id };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/arrivals/incoterms`, data, { headers });
  }

  /**
   * KPI/riepiloghi degli arrivi sull'intero set filtrato (stessi filtri della lista).
   */
  getArrivalsKpis(filters: {
    arrFrom?: string | null; arrTo?: string | null;
    depFrom?: string | null; depTo?: string | null;
    tma?: string[] | null; iec?: string[] | null;
    sectorKeys?: (string | number)[] | null; nationCodes?: string[] | null;
    incoterms?: string[] | null; consigneeLike?: string | null; consignee?: string | null;
  }) {
    const data = {
      userKey: this.storageService.getUserStorage()?.id,
      arrFrom: filters.arrFrom ?? null,
      arrTo:   filters.arrTo   ?? null,
      depFrom: filters.depFrom ?? null,
      depTo:   filters.depTo   ?? null,
      tma:     filters.tma     ?? null,
      iec:     filters.iec     ?? null,
      incoterms: filters.incoterms ?? null,
      consigneeLike: filters.consigneeLike ?? null,
      consignee: filters.consignee ?? null,
      sectorKeys:  filters.sectorKeys  ?? null,
      nationCodes: filters.nationCodes ?? null
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/arrivals/kpis`, data, { headers });
  }

  /**
   * Richiesta accesso al dettaglio completo di una pratica in arrivo (evento CRM tipo 9).
   * Il backend risolve i sales destinatari e invia loro l'email con il link di approvazione.
   */
  requestArrivalDetailAccess(praticheKey: number, typearch: string | null, job: string | null, customerName: string | null, note: string | null) {
    const data = {
      userKey: this.storageService.getUserStorage()?.id,
      praticheKey, typearch: typearch ?? null, job: job ?? null,
      customerName: customerName ?? null, note: note ?? null
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/arrivals/detail-request/save`, data, { headers });
  }

  /**
   * Dettaglio completo pratica (gated server-side: accesso solo se approvato/sales/superadmin).
   * tipoTrasporto seleziona la variante dcs-lib (Mare/Aereo/Terra).
   */
  getArrivalDetail(praticheKey: number, typearch: string | null, tipoTrasporto: string | null) {
    const data = {
      userKey: this.storageService.getUserStorage()?.id,
      praticheKey, typearch: typearch ?? null, tipoTrasporto: tipoTrasporto ?? null
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/arrivals/detail`, data, { headers });
  }

  /** Pagina esterna: carica i dati della richiesta accesso pratica (no login, via paramId). */
  loadArrivalDetailRequest(uuid_key: string) {
    const data = { uuid_key };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/arrivals/detail-request/load`, data, { headers });
  }

  /** Pagina esterna: approva(1)/rifiuta(2) la richiesta accesso pratica (via adminKey cifrato). */
  changeArrivalDetailRequestStatus(uuid_key: string, status: number, adminKey: string | null, note_fine: string | null) {
    const data = { uuid_key, status, adminKey: adminKey ?? null, note_fine: note_fine ?? null };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/arrivals/detail-request/status/change`, data, { headers });
  }

  /** Storico richieste accesso dettaglio per una pratica (utente loggato). */
  getArrivalDetailRequests(praticheKey: number) {
    const data = { praticheKey };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/arrivals/detail-request/history`, data, { headers });
  }

  getDashboardSalesByCustomer(filters: {
    dateFrom?: string | null;
    dateTo?: string | null;
    dateJobFrom?: string | null;
    dateJobTo?: string | null;
    instName?: string | null;
    officeKey?: number | null;
    salesKey?: number | null;
    clianaKey?: number | null;
    attributionMode?: 'sales' | 'creator' | null;
    /** Modi di trasporto: sottoinsieme di 'M','A','T'. Vuoto/assente = tutti. */
    tmaList?: string[] | null;
  }) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/dashboard/sales/by-customer`, filters, { headers });
  }

  /**
   * Dashboard Vendite — lista clienti definitivi (cliana_tipo='1') assegnati a un sales.
   * Usato dal cascade della dashboard-sales per popolare la dropdown Customer.
   * NB: query ad-hoc (db_dashboard_sales.getCustomersForDashboardSales) — NON riusa
   * il generico CRM_ROUTE_CUSTOMERS_LIST per evitare il vincolo has_recent_practices
   * e lo switch per flgUserType.
   */
  getDashboardSalesCustomers(filters: {
    salesKey?: number | null;
    instName?: string | null;
    officeKey?: number | null;
    search?: string | null;
    attributionMode?: 'sales' | 'creator' | null;
  }) {
    const data = {
      salesKey:        filters.salesKey        ?? null,
      instName:        filters.instName        ?? null,
      officeKey:       filters.officeKey       ?? null,
      search:          filters.search          ?? '',
      attributionMode: filters.attributionMode ?? 'sales',
      default_installation: this.storageService.getUserStorage()?.default_installation
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/dashboard/sales/customers/list`, data, { headers });
  }

  /**
   * Dashboard Vendite — popola il dropdown Sales/Creator con SOLO gli utenti
   * effettivamente coinvolti (sale_manager+clistad oppure creator) sulle pratiche
   * dell'installation/office nel range date. Coerente con la chip Sales/Creator.
   */
  getDashboardSalesUsers(filters: {
    attributionMode: 'sales' | 'creator';
    dateFrom?: string | null;
    dateTo?: string | null;
    dateJobFrom?: string | null;
    dateJobTo?: string | null;
    instName?: string | null;
    officeKey?: number | null;
    search?: string | null;
    top?: number | null;
    tmaList?: string[] | null;
  }) {
    const data = {
      attributionMode: filters.attributionMode || 'sales',
      dateFrom:    filters.dateFrom    ?? null,
      dateTo:      filters.dateTo      ?? null,
      dateJobFrom: filters.dateJobFrom ?? null,
      dateJobTo:   filters.dateJobTo   ?? null,
      instName:    filters.instName    ?? null,
      officeKey:   filters.officeKey   ?? null,
      search:      filters.search      ?? '',
      top:         filters.top         ?? 100,
      tmaList:     filters.tmaList     ?? null
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/dashboard/sales/users/list`, data, { headers });
  }

  /**
   * Dashboard Vendite — export Excel (stessi filtri della search).
   * Stessa rotta `dashboard/sales/by-customer` ma con `format: 'excel'`.
   * Risposta: blob (xlsx) con header Content-Disposition.
   */
  exportDashboardSalesByCustomer(filters: {
    dateFrom?: string | null;
    dateTo?: string | null;
    dateJobFrom?: string | null;
    dateJobTo?: string | null;
    instName?: string | null;
    officeKey?: number | null;
    salesKey?: number | null;
    clianaKey?: number | null;
    attributionMode?: 'sales' | 'creator' | null;
    instLabel?: string | null;
    officeLabel?: string | null;
    salesLabel?: string | null;
    customerLabel?: string | null;
    visibleColumns?: string[];
    generatedBy?: string;
    tmaList?: string[] | null;
  }): Observable<any> {
    const body: any = {
      dateFrom:    filters.dateFrom    ?? null,
      dateTo:      filters.dateTo      ?? null,
      dateJobFrom: filters.dateJobFrom ?? null,
      dateJobTo:   filters.dateJobTo   ?? null,
      instName:    filters.instName    ?? null,
      officeKey:   filters.officeKey   ?? null,
      salesKey:    filters.salesKey    ?? null,
      clianaKey:   filters.clianaKey   ?? null,
      attributionMode: filters.attributionMode ?? 'sales',
      tmaList:     filters.tmaList     ?? null,
      // Echo human-readable filter labels for the Excel header
      officeName: filters.officeLabel   ?? null,
      salesName:  filters.salesLabel    ?? null,
      clianaName: filters.customerLabel ?? null,
      visibleColumns: filters.visibleColumns,
      generatedBy:    filters.generatedBy,
      format: 'excel'
    };
    return this.http.post(`${this.apiUrl}/dashboard/sales/by-customer`, body, {
      responseType: 'blob',
      observe: 'response'
    });
  }

  checkViesVatNumber(countryCode, vatNumber) {
    const data = { countryCode: countryCode, vatNumber: vatNumber };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/vat/vies/check`, data, {
      headers
    });
  }

  checkVatNumber(idTab, nationCode, vatNumber) {
    const data = { idTab: idTab, nationCode: nationCode, vatNumber: vatNumber };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/vat/existence/check`, data, {
      headers
    });
  }

  checkCustomerExistence(userName: any, customerObject: any) {
    const data = { userName: userName, customerObject: customerObject };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/checkExistence`, data, {
      headers
    });
  }

  listEvents(userKey, clianaKey = undefined, futureEvents = 0, pageSize = 1000, page = 1, searchQuery = '', headBranches=undefined, default_installation = undefined) {
    const data = {
      idtab: this.storageService.getIdTab("cliana"),
      userKey: userKey,
      clianaKey: clianaKey,
      future: futureEvents,
      pageSize: pageSize,
      page: page,
      searchQuery: searchQuery,
      headBranches: headBranches,
      default_installation: default_installation
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/events/list/table`, data, {
      headers
    });
  }

  listEventsType() {
    const user = this.storageService.getUserStorage();
    if (!user) {
      console.error('listEventsType: No user in storage');
      return throwError(() => new Error('User not authenticated'));
    }
    const data = {
      idtab: this.storageService.getIdTab("cliana"),
      userKey: user.id
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/events/list/types`, data, {
      headers
    });
  }

  updateEvent(eventObject) {
    const data = {
      idtab: this.storageService.getIdTab("cliana"),
      userKey: this.storageService.getUserStorage()?.id,
      eventDetail: eventObject
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/events/update`, data, { headers });
  }

  listCustomerSalesManager(userId, clianaKey = undefined) {
    let fromDate = new Date();
    fromDate.setFullYear(fromDate.getFullYear() - 1);
    fromDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
    let toDate = new Date();

    const data = {
      userKey: userId,
      fromDate: fromDate,
      toDate: toDate,
      clianaKey: clianaKey
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(
      `${this.apiUrl}/user/load/configs/customer_sales_manager`,
      data,
      { headers }
    );
  }

  // Lightweight per-customer jobs aggregate for a sales user (User Performance page).
  // Replaces the heavy listCustomers(pageSize=1M) enrichment: single GROUP BY, fast.
  listCustomerJobsAggregate(userId) {
    const data = { userKey: userId };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(
      `${this.apiUrl}/user/load/configs/customer_jobs_aggregate`,
      data,
      { headers }
    );
  }

  // Analogo jobpra del precedente: aggregato per-cliente da dcs_JOBPRA per i sales
  // di filiali "no_jobs" (CCS/FIO). Filtra per UTE_SALES (username) + DITTA, stesso
  // shape di output, cosi' User Performance riusa lo stesso enrichment/Top Accounts.
  listCustomerJobsAggregateJobpra(salesUsername, ditta) {
    const data = { salesUsername, ditta };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(
      `${this.apiUrl}/user/load/configs/customer_jobs_aggregate_jobpra`,
      data,
      { headers }
    );
  }

  // Trend mensile (ultimi 13 mesi) jobs+TEU da dcs_JOBPRA per il chart annuale jobpra.
  // Filtra per UTE_SALES (username) + DITTA. Ritorna [{ year, month(1-12), jobs, teus }].
  getSalesMonthlyTrendJobpra(salesUsername, ditta) {
    const data = { salesUsername, ditta };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(
      `${this.apiUrl}/user/load/configs/sales_monthly_trend_jobpra`,
      data,
      { headers }
    );
  }

  listJobSaleManager(userId, branch) {
    let fromDate = new Date();
    fromDate.setFullYear(fromDate.getFullYear() - 1);
    fromDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
    let toDate = new Date();

    const data = {
      userKey: userId,
      branch: branch,
      fromDate: fromDate,
      toDate: toDate
    };
    
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(
      `${this.apiUrl}/user/load/configs/job_sales_manager`,
      data,
      { headers }
    );
  }

  listQuotesRecord(userId, branch) {
    let fromDate = new Date();
    fromDate.setFullYear(fromDate.getFullYear() - 1);
    fromDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
    let toDate = new Date();

    const data = {
      userKey: userId,
      branch: branch,
      fromDate: fromDate,
      toDate: toDate
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(
      `${this.apiUrl}/user/load/configs/quotes_record`,
      data,
      { headers }
    );
  }

  listJobsCntrSalesManager(userId, branch, rangesDate) {
    const data = {
      userKey: userId,
      branch: branch,
      rangesData: rangesDate
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(
      `${this.apiUrl}/user/load/configs/job_cntr_sales_manager`,
      data,
      { headers }
    );
  }

  listSearchUsers(page, pageSize, querySearch, id, branch, onlyUsers =  false, active=false, office = undefined, crmStatus = 'all') {
    const user = this.storageService.getUserStorage();
    let userId = user?.id;
    let isAdmin = user?.flgUserType === 'A' || user?.flgUserType === 'S';
    let head_branches = user?.head_branches;

    const data = {
      idtab: this.storageService.getIdTab("cliana"),
      page: page,
      pageSize: pageSize,
      search: querySearch,
      id: id,
      userKey: userId,
      isAdmin: isAdmin,
      branch: branch,
      office: office,  // 🆕 Nuovo parametro office
      //head_branches: head_branches,
      onlyUsers: onlyUsers,
      active: active,
      crmStatus: crmStatus // 🆕 Nuovo parametro crmStatus ('all', 'yes', 'no')
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/list/users`, data, { headers });
  }
 
  listBranches() {
    const data = {
      idtab: this.storageService.getIdTab("cliana"),
      userKey: this.storageService.getUserStorage()?.id
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/list/branches`, data, {
      headers
    });
  }

  /**
   * 🆕 Lista degli offices (filiali) per il filtro SuperAdmin
   * @param installationCode - Codice installation per filtrare gli offices (opzionale)
   */
  listOffices(installationCode: string | string[] = null) {
    // 🔁 Retrocompat: serializza array → CSV. Backend accetta entrambi i formati.
    const installationCodeSerialized = Array.isArray(installationCode)
      ? installationCode.filter(Boolean).join(',')
      : installationCode;
    const data = {
      userKey: this.storageService.getUserStorage()?.id,
      installationCode: installationCodeSerialized
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/list/offices`, data, {
      headers
    });
  }

  listQuotes(userKey, userFilter, clianaKey, pageSize, pageNumber, searchQuery, context = 'customer', branchKey = undefined) {
    const data = {
      userKey: userKey,        // Utente in sessione (per configurazioni/permessi)
      userFilter: userFilter,  // Utente per cui filtrare le quotazioni
      clianaKey: clianaKey,
      pageSize: pageSize,
      page: pageNumber,
      searchQuery: searchQuery,
      context: context,
      branchKey: branchKey     // Branch per SuperAdmin
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/list/quotes`, data, { headers });
  }

  listQuotesCompanies(quoteKey) {
    const data = {
      quoteKey: quoteKey
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/list/quotes/companies`, data, { headers });
  }

  listQuotesCompaniesBatch(quoteKeys: number[]) {
    const data = {
      quoteKeys: quoteKeys
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/list/quotes/companies`, data, { headers });
  }

  /** Conteggio job (pratiche) ATTIVI collegati per ciascuna quotazione (batch per-pagina). */
  listQuotesJobsCount(quoteKeys: number[]) {
    const data = {
      quoteKeys: quoteKeys
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/list/quotes/jobs-count`, data, { headers });
  }

  listQuotesAdmin(userKey, branchKey, context = 'admindashboard') {
    const data = {
      userKey: userKey,
      branchKey: branchKey,
      context: context
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/quotes`, data, { headers });
  }

  listJobsOverview(userKey, clianaKey, dateBasis?: 'competence' | 'creation') {
    // dateBasis assente = competenza (comportamento storico, anche per user-detail).
    const data = { userKey: userKey, clianaKey: clianaKey, dateBasis };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/list/jobs/overview`, data, {
      headers
    });
  }

  selectLastJob(clianaKey) {
    const data = { clianaKey: clianaKey };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/select/last_job`, data, {
      headers
    });
  }

  loadEmails(clianaKey, userKey?: number, userType?: string) {
    const data = { 
      clianaKey: clianaKey,
      userKey: userKey || null,
      userType: userType || null
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/contact/list`, data, {
      headers
    });
  }

  updateEmailObject(emailObject) {
    const data = {
      userKey: this.storageService.getUserStorage()?.id,
      emailObject: emailObject
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/contact/save`, data, { headers });
  }

  deleteContactObject(clianaKey: number, progressive: number) {
    const data = {
      clianaKey: clianaKey,
      contactPrg: progressive
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/contact/delete`, data, { headers });
  }

  listContactsLog(clianaKey) {
    // Lo storico contatti e' riservato al SuperAdmin: l'identita' va passata al
    // backend, che rifiuta la richiesta se userType !== 'S'.
    const user = this.storageService.getUserStorage();
    const data = {
      clianaKey: clianaKey,
      userKey: user?.id || null,
      userType: user?.flgUserType || null
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/contact/log/actions`, data, {
      headers
    });
  }

  requestSalesManagerCustomer(branchKey, userKey, requestDetail) {
    const data = {
      userKey: userKey,
      requestDetail: requestDetail,
      branchKey: branchKey
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/sales/request/save`, data, { headers });
  }

  // Parte C — Richiesta di estensione scadenza assegnazione (utente normale → approvazione SuperAdmin)
  requestExtendExpiry(branchKey, userKey, requestDetail) {
    const data = {
      userKey: userKey,
      requestDetail: requestDetail,
      branchKey: branchKey
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/sales/request/extend/save`, data, { headers });
  }

  // Parte C — Lookup: gestione scadenze abilitata per l'installazione del cliente?
  isClistadExpiryEnabled(clianaKey) {
    const data = { clianaKey: clianaKey };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/sales/expiry/enabled`, data, { headers });
  }

  // Parte C — Estensione diretta della scadenza (SOLO SuperAdmin, nessuna approvazione)
  extendExpiryDirect(clianaKey, iec, tma, ownerUserKey, newToDate, userName, userKey = null, note = null) {
    const data = {
      cliana_key: clianaKey,
      iec: iec,
      tma: tma,
      ownerUserKey: ownerUserKey,
      newToDate: newToDate,
      userName: userName,
      userKey: userKey,
      note: note
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/sales/manager/extend-direct`, data, { headers });
  }

  /**
   * Storico delle richieste del cliente — registrazione (tipo 7), richiesta
   * gestione (5), estensione assegnazione (8) — aggregato su TUTTE le
   * installazioni in cui esiste lo stesso soggetto (match per P.IVA o DUNS).
   * Riservato al SuperAdmin: il backend rifiuta se userType !== 'S'.
   */
  listCustomerRequestsHistory(clianaKey: number): Observable<any> {
    const user = this.storageService.getUserStorage();
    const data = {
      clianaKey: clianaKey,
      userKey: user?.id || null,
      userType: user?.flgUserType || null
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/requests/history`, data, { headers })
      .pipe(timeout(30000));
  }

  requestSalesManagerCustomerList(customerKey) {
    const data = {
      customerKey: customerKey,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/sales/request/list`, data, { headers });
  }

  requestSalesManagerCustomerLoad(uuidKey, adminKey: string = null) {
    const data: any = {
      uuid_key: uuidKey,
    };
    if (adminKey) {
      data.adminKey = adminKey;
    }
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/sales/request/load`, data, { headers });
  }

  requestSalesManagerCustomerChangeStatus(uuidKey, status, eventKey = undefined, noteFine: string = '', adminKey: string = null) {
    const userData = this.storageService.getUserStorage();
    const data = {
      uuid_key: uuidKey,
      status: status,
      eventKey: eventKey,
      note_fine: noteFine,
      adminKey: adminKey,
      userKey: userData?.id || null
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/sales/request/status/change`, data, { headers });
  }

  listSalesRequests(filters: any = {}, page: number = 1, pageSize: number = 10): Observable<any> {
    const userData = this.storageService.getUserStorage();
    const userKey = userData?.id;
    
    const payload = {
      filiale: filters.branch || undefined,
      installation: filters.installation || undefined,
      idIntestatario: filters.idIntestatario || undefined,
      searchQuery: filters.searchQuery || undefined,
      status: filters.status || undefined,
      tipoEvento: filters.tipoEvento || undefined,
      userKey: userKey,
      page: page,
      pageSize: pageSize
    };

    return this.http.post(`${this.apiBaseUrl}crm/sales/requests/list`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  generatePDFQuote(rifKey: number, cmpKey: number): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    const body = { rifKey, cmpKey };
    return this.http.post(`${this.apiUrl}/quote/print`, body, {
      headers,
      responseType: 'json' // Cambiato da 'blob' a 'json'
    });
  }
  

  listCustomerSalesConfig(clianaKey, bypassCache = false) {
    const data = {
      customerId: clianaKey,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    
    // Aggiungi parametro bypass_cache se richiesto
    const url = bypassCache 
      ? `${this.apiUrl}/customers/sales_config/list?bypass_cache=true`
      : `${this.apiUrl}/customers/sales_config/list`;
    
    return this.http.post(url, data, { headers });
  }


  // 🎭 MOCK: Company Validation - Uses mock data endpoints
  listCustomerCreditSafe(nation_code, company_name, vat_namber) {
    const data = {
      nation_code: nation_code,
      company_name: company_name,
      vat_namber: vat_namber,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    // Using MOCK endpoint for testing with realistic data
    return this.http.post(`${this.apiUrl}/admin/credit_safe/mock/search`, data, { headers });
  }

  // 🎭 MOCK: Company Validation Detail - Uses mock data with real PDF
  detailCustomerCreditSafe(customerCreditSafeId) {
    const data = {
      customerCreditSafeId: customerCreditSafeId,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    // Using MOCK endpoint for testing with real PDF report
    return this.http.post(`${this.apiUrl}/admin/credit_safe/mock/detail`, data, { headers })
      .pipe(
        timeout(60000) // 60 seconds timeout
      );
  }

  // 🇮🇹 CERVED API METHODS
  
  /**
   * Cerca aziende nel database Cerved (REAL API)
   * @param vat_number Partita IVA
   * @param company_name Nome azienda
   * @param nation_code Codice nazione (IT, ES, DE, etc.)
   */
  cervedSearchCompany(vat_number: string, company_name: string, nation_code?: string) {
    const data = {
      vat_number: vat_number,
      company_name: company_name,
      nation_code: nation_code || 'IT' // Default IT per backward compatibility
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/cerved/search`, data, { headers })
      .pipe(
        timeout(30000) // 30 seconds timeout
      );
  }

  /**
   * 🛒 CERVED: Purchase Full Report PDF
   * Acquista il Full Report PDF per un soggetto Cerved
   * @param userId ID utente (per tracciamento acquisti)
   */
  cervedPurchaseFullReport(subjectId: number | null, vatNumber?: string, taxCode?: string, customerId?: number, userId?: number): Observable<any> {
    const data = {
      subjectId: subjectId,
      vatNumber: vatNumber,
      taxCode: taxCode,
      customerId: customerId,
      userId: userId
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/cerved/purchase-report`, data, { headers })
      .pipe(
        timeout(600000) // 10 minuti - Cerved può impiegare molto tempo per generare report
      );
  }

  /**
   * 🔄 CERVED ASYNC: Get company detail with background processing
   * For long-running operations that may exceed Netlify timeout
   * Returns immediately with polling info if cache miss
   */
  cervedGetCompanyDetailAsync(cerved_id: number, vatNumber?: string, userId?: number): Observable<any> {
    const data = {
      cerved_id: cerved_id,
      subject_id: cerved_id,
      include_score: true,
      include_negative_events: true,
      include_balance_sheets: false,
      vat_number: vatNumber,
      user_id: userId
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/cerved/detail-async`, data, { headers })
      .pipe(
        timeout(30000) // 30s - should return quickly with polling info or cached data
      );
  }

  /**
   * 🔄 CERVED STATUS: Poll for async operation status
   * Call this repeatedly until status is 'completed' or 'error'
   */
  cervedGetStatus(storageKey: string): Observable<any> {
    const data = {
      storageKey: storageKey
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/cerved/status`, data, { headers })
      .pipe(
        timeout(30000) // 30s - la risposta "completed" contiene il payload CervedDetailFull completo
      );
  }

  /**
   * 🔍 CERVED CHECK CACHE: Verifica se esiste report in cache (solo DB, veloce)
   * Usato per decidere se mostrare il pulsante - NON scarica il PDF
   * @param vatNumber VAT number (required)
   * @param subjectId Cerved subject ID (optional)
   */
  cervedCheckCacheExists(vatNumber: string, subjectId?: number): Observable<any> {
    const data = {
      vat_number: vatNumber,
      subject_id: subjectId
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/cerved/check-cache`, data, { headers })
      .pipe(
        timeout(5000) // 5s timeout - solo query DB
      );
  }

  /**
   * 📄 CERVED CACHED PDF: Get PDF from cache only (for simple users)
   * Does NOT make new purchases - returns PDF only if valid cache exists
   * @param vatNumber VAT number (required)
   * @param subjectId Cerved subject ID (optional, improves search)
   */
  cervedGetCachedPdf(vatNumber: string, subjectId?: number): Observable<any> {
    const data = {
      vat_number: vatNumber,
      subject_id: subjectId
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/cerved/cached-pdf`, data, { headers })
      .pipe(
        timeout(30000) // 30s timeout
      );
  }

  // ============================================================================
  // CERVED V2 - Nuove rotte basate su SQL state machine (no Netlify Blobs)
  // Garanzia anti double-purchase via unique constraint per VAT (migration 006)
  // Always-enforce JWT, retrieve idempotente Cerved (gratis), polling con backoff
  // ============================================================================

  /**
   * 🔍 CERVED V2 CHECK CACHE: verifica esistenza report in cache per VAT (solo DB)
   * Sostituisce cervedCheckCacheExists. Lookup per VAT esclusivo.
   * @param vatNumber Partita IVA (required, chiave business)
   */
  cervedV2CheckCacheExists(vatNumber: string): Observable<any> {
    const data = { vatNumber };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiBaseUrl}cerved-v2/check-cache`, data, { headers })
      .pipe(
        timeout(5000) // 5s - solo query DB
      );
  }

  /**
   * 🔄 CERVED V2 DETAIL ASYNC: avvia recupero detail XML (cache-first)
   * Sostituisce cervedGetCompanyDetailAsync.
   * Ritorna sempre rapidamente (<5s) con:
   *   - status:'completed' + fromCache:true se cache hit (zero crediti)
   *   - status:'processing' + purchaseId + needsPolling:true se nuovo acquisto
   * Il payload XML NON viene incluso: il client polla /status per ottenerlo.
   * @param vatNumber Partita IVA (required, chiave business)
   * @param subjectId ID Cerved (richiesto per nuovo acquisto, opzionale per cache hit)
   * @param userId ID utente (per tracking)
   */
  cervedV2GetCompanyDetailAsync(vatNumber: string, subjectId?: number, userId?: number): Observable<any> {
    const data = { vatNumber, subjectId, userId };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiBaseUrl}cerved-v2/detail-async`, data, { headers })
      .pipe(
        timeout(30000) // 30s - response veloce con RealTimeDoc=false
      );
  }

  /**
   * 🔄 CERVED V2 STATUS: polling stato per operazione async
   * Sostituisce cervedGetStatus. Riceve purchaseId (numero) invece di storageKey (stringa).
   * Effettua internamente retrieve idempotente Cerved (gratis) quando DEFERRED scade.
   * @param purchaseId ID riga DB ritornato da /detail-async o /purchase-report
   * @param format 'XML' (per detail) o 'PDF' (per purchase-report)
   */
  cervedV2GetStatus(purchaseId: number, format: 'XML' | 'PDF' = 'XML'): Observable<any> {
    const data = { purchaseId, format };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiBaseUrl}cerved-v2/status`, data, { headers })
      .pipe(
        timeout(30000) // 30s - include retrieve Cerved + parse XML eventuale
      );
  }

  /**
   * 🛒 CERVED V2 PURCHASE-REPORT: rotta unificata cache-first per il PDF
   * Sostituisce sia cervedPurchaseFullReport sia cervedGetCachedPdf.
   * Comportamento smart:
   *   - Cache hit -> pdfBase64 nella response immediata (zero crediti)
   *   - Cache miss + subjectId presente -> nuovo acquisto, response 'deferred' + polling
   *   - Cache miss + subjectId mancante -> 400 NEED_SUBJECT_ID (link esterno con cache scaduta)
   * @param vatNumber Partita IVA (required)
   * @param subjectId ID Cerved (richiesto per nuovo acquisto; null se cache-only)
   * @param userId ID utente
   */
  cervedV2PurchaseFullReport(vatNumber: string, subjectId?: number | null, userId?: number, uuidKey?: string | null): Observable<any> {
    const data: any = { vatNumber, subjectId, userId };
    // Quando la chiamata parte da external-detail-request (link email senza login)
    // passiamo uuid_key per consentire al backend di bypassare il check JWT
    // dopo aver validato l'accesso contro DB.
    if (uuidKey) data.uuid_key = uuidKey;
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiBaseUrl}cerved-v2/purchase-report`, data, { headers })
      .pipe(
        timeout(30000) // 30s - response veloce: cache hit con PDF o avvio DEFERRED
      );
  }

  deleteSalesCustomer(clistad_key, userKeyRemoval: number = 0) {
    const data = {
      clistad_key: clistad_key,
      userKeyRemoval: userKeyRemoval,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/sales/request/sales/delete`, data, { headers });
  }

  addSalesManagerDirect(cliana_key, userkey, username, iec, tma, admin_user_id, note: string = '') {
    const data: any = {
      cliana_key: cliana_key,
      userkey: userkey,
      username: username,
      iec: iec,
      tma: tma,
      admin_user_id: admin_user_id
    };
    // Aggiungi la nota se presente
    if (note && note.trim()) {
      data.note = note.trim();
    }
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/sales/manager/add`, data, { headers });
  }

  assignSalesBulk(cliana_keys: number[], userkey: number, username: string, iec: string, tma: string, admin_user_id: number, note: string = '') {
    const data: any = {
      cliana_keys: cliana_keys,
      userkey: userkey,
      username: username,
      iec: iec || '',
      tma: tma || '',
      admin_user_id: admin_user_id
    };
    if (note && note.trim()) {
      data.note = note.trim();
    }
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/sales/manager/add-bulk`, data, { headers });
  }

  deleteSalesBulk(cliana_keys: number[], user_key: number, userKeyRemoval: number) {
    const data = {
      cliana_keys: cliana_keys,
      user_key: user_key,
      userKeyRemoval: userKeyRemoval
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/sales/request/sales/delete-bulk`, data, { headers });
  }

  listSalesManagersByCustomers(cliana_keys: number[]) {
    const data = { cliana_keys: cliana_keys };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/sales/managers-by-customers`, data, { headers });
  }

  requestIA(conversationHistory: PayloadIA[]) {
    const data = {
      conversationHistory: conversationHistory,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/ia/request`, data, { headers });
  }


  /*********************DATAMYNE******************** */


  listNations() {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/nationList`, {}, { headers });
  }

  

  listApplications() {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/datamyne/applications`, {}, { headers });
  }

  
  getApplicationConfig(idApplication) {
    const data = {
      idApplication: idApplication,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/datamyne/application`, data, { headers });
  }


  getTotalsBy(idApp, fromDate, toDate, pageSize, pageNumber, idTotalsBy, filterList) {
    const data = {
      idApplication: idApp,
      fromDate: fromDate,
      toDate: toDate,
      pageSize: pageSize,
      pageNumber: pageNumber,
      idTotalsBy: idTotalsBy,
      filterList: filterList,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/datamyne/totalsby`, data, { headers });
  }


  getResults(idApplication, fromDate, toDate, pageSize, pageNumber, filterList) {
    const data = {
      idApplication: idApplication,
      fromDate: fromDate,
      toDate: toDate,
      pageSize: pageSize,
      pageNumber: pageNumber,
      filterList: filterList,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/datamyne/results`, data, { headers });
  }

  getDetail(idApplication, keyValues) {
    const data = {
      idApplication: idApplication,
      keyValues: keyValues,
     
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/datamyne/details`, data, { headers });
  }


  getVisualize(idApplication, fromDate, toDate, pageSize, pageNumber, filterList) {
    const data = {
      idApplication: idApplication,
      fromDate: fromDate,
      toDate: toDate,
      pageSize: pageSize,
      pageNumber: pageNumber,
      filterList: filterList,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/datamyne/visualize`, data, { headers });
  }

   /*********************DATAMYNE******************** */

  // API per il dashboard amministrativo
  
  /**
   * Ottiene statistiche sui job raggruppate per branch
   * @param branchKey Opzionale, filtra per uno specifico branch
   */
  getDashboardJobs(branchKey?: string) {
    const user = this.storageService.getUserStorage();
    const data = {
      branchKey: branchKey || undefined, 
      v: new Date().getTime(), // Parametro di versione per evitare caching
      userKey: user?.id || ''
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/dashboard/list/jobs`, data, { headers });
  }

  /**
   * Ottiene statistiche sui clienti raggruppate per branch
   * @param branchKey Opzionale, filtra per uno specifico branch
   */
  getDashboardCustomers(branchKey?: string) {
    const user = this.storageService.getUserStorage();
    const data = {
      branchKey: branchKey || undefined, 
      v: new Date().getTime(), // Parametro di versione per evitare caching
      userKey: user?.id || ''
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/dashboard/list/customers`, data, { headers });
  }

  /**
   * Ottiene statistiche sulle quotazioni raggruppate per branch
   * @param branchKey Opzionale, filtra per uno specifico branch
   * @param includeRawData Opzionale, se includere i dati grezzi nella risposta
   */
  getDashboardQuotes(branchKey?: string, includeRawData: boolean = true) {
    const user = this.storageService.getUserStorage();
    const data = {
      branchKey: branchKey || undefined,
      v: new Date().getTime(), // Parametro di versione per evitare caching
      userKey: user?.id || '',
      includeRawData: includeRawData
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/dashboard/list/quotes`, data, { headers });
  }

  /**
   * Ottiene statistiche sugli eventi raggruppate per branch
   * @param branchKey Opzionale, filtra per uno specifico branch
   */
  getDashboardEvents(branchKey?: string) {
    const user = this.storageService.getUserStorage();
    const data = {
      branchKey: branchKey || undefined, 
      v: new Date().getTime(), // Parametro di versione per evitare caching
    };
    
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/dashboard/list/events`, data, { headers });
  }

  /**
   * Ottiene la lista dei branch con statistiche
   * @param branchKey Opzionale, filtra per uno specifico branch
   */
  getDashboardBranches(branchKey?: string) {
    const user = this.storageService.getUserStorage();
    const data = {
      branchKey: branchKey || undefined, 
      v: new Date().getTime(), // Parametro di versione per evitare caching
      userKey: user?.id || ''
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/dashboard/list/branches`, data, { headers });
  }

  /**
   * Ottiene statistiche sugli utenti raggruppate per branch
   * @param branchKey Opzionale, filtra per uno specifico branch
   */
  getDashboardUsers(branchKey?: string) {
    const user = this.storageService.getUserStorage();
    const data = {
      branchKey: branchKey || undefined, 
      v: new Date().getTime(), // Parametro di versione per evitare caching
      userKey: user?.id || ''
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/dashboard/list/users`, data, { headers });
  }

  /**
   * Ottiene i top 10 clienti con maggiore crescita nell'ultimo trimestre
   * @param branchKey Branch da analizzare
   * @param criterio Criterio di crescita: 'jobs', 'containers' o 'colis'
   */
  getDashboardTopGrowingCustomers(branchKey: string, criterio: 'jobs' | 'containers' | 'colis' = 'jobs', comparisonType: string = 'month-complete') {
    const user = this.storageService.getUserStorage();
    const data = {
      branchKey: branchKey,
      criterio: criterio,
      comparisonType: comparisonType,
      v: new Date().getTime(), // Parametro di versione per evitare caching
      userKey: user?.id || ''
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/dashboard/list/topGrowingCustomers`, data, { headers });
  }

  /**
   * Ottiene i top 10 clienti con maggiore crescita nell'ultimo trimestre
   * @param branchKey Branch da analizzare
   * @param criterio Criterio di crescita: 'jobs', 'containers' o 'colis'
   */
  getDashboardTopGrowingSales(branchKey: string, criterio: 'jobs' | 'containers' | 'colis' = 'jobs', comparisonType: string = 'month-complete') {
    const user = this.storageService.getUserStorage();
    const data = {
      branchKey: branchKey,
      criterio: criterio,
      comparisonType: comparisonType,
      v: new Date().getTime(), // Parametro di versione per evitare caching
      userKey: user?.id || ''
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/dashboard/list/topGrowingSales`, data, { headers });
  }

  /**
   * Ottiene i top 10 uffici con maggiore crescita nell'ultimo trimestre
   * @param branchKey Branch da analizzare
   * @param criterio Criterio di crescita: 'jobs', 'containers' o 'colis'
   */
  getDashboardTopGrowingOffices(branchKey: string, criterio: 'jobs' | 'containers' | 'colis' = 'jobs', comparisonType: string = 'month-complete') {
    const user = this.storageService.getUserStorage();
    const data = {
      branchKey: branchKey,
      criterio: criterio,
      comparisonType: comparisonType,
      v: new Date().getTime(), // Parametro di versione per evitare caching
      userKey: user?.id || ''
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/dashboard/list/topGrowingOffices`, data, { headers });
  }

  getDashboardLatestEvents(branchKey?: string) {
    const user = this.storageService.getUserStorage();
    const data = {
      branchKey: branchKey,
      v: new Date().getTime(), // Parametro di versione per evitare caching
      userKey: user?.id || ''
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/dashboard/list/latestEvents`, data, { headers });
  }
 
  getCustomerStatistics(customerKey: number, dateBasis?: 'competence' | 'creation') {
    // dateBasis assente = competenza. La creazione vale solo se la SP ha la migration
    // 029: la base EFFETTIVA torna nella risposta (date_basis).
    const data = {
      customerKey: customerKey,
      dateBasis,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/get/statistics`, data, { headers });
  }

  /**
   * Statistiche volumi cliente da dcs_JOBPRA (installazioni no-jobs: CCS, FIO, ...).
   * Alternativa a getCustomerStatistics: bypassa la SP DCS e legge inline dal nostro import.
   */
  getCustomerStatisticsJobpra(clianaKey: number, ditta: string) {
    const data = {
      clianaKey: clianaKey,
      ditta: ditta,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/get/statistics/jobpra`, data, { headers });
  }

  /**
   * Home dashboard per filiali "import-only" che leggono spedizioni da dcs_JOBPRA.
   * Replica le KPI di cmp-users-total-cards (Containers/TEUs/Air KG/Active Customers)
   * + widget commerciali Mix I/E e Top 5 Trade Lanes.
   *
   * @param ditta          Codice filiale (es. 'CCS', 'FIO')
   * @param rangesDate     Stesso schema di cmp-users-total-cards.getDateRanges()
   * @param salesUsername  Opzionale, filtra per UTE_SALES (utenti operativi)
   */
  getHomeStatisticsJobpra(ditta: string, rangesDate: any, salesUsername?: string | null) {
    const data = {
      ditta: ditta,
      rangesDate: rangesDate,
      salesUsername: salesUsername || null,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/home/get/statistics/jobpra`, data, { headers });
  }

  /**
   * Compare statistiche volumi di UN cliente su PIU installazioni (super admin).
   * requests: [{clianaKey, installationCode, installationName?}]
   */
  getCustomerStatisticsCompare(requests: Array<{ clianaKey: number; installationCode: string; installationName?: string | null }>,
                               dateBasis?: 'competence' | 'creation') {
    const data = { requests, dateBasis };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/get/statistics/compare`, data, { headers });
  }

  loadJobsCountCustomer(userKey: number, customerKey: number) {
    const data = {
      userKey: userKey,
      customerKey: customerKey,
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customer/load/jobsCount`, data, { headers });
  }

  getMenuItems(username: string, installation: string): Observable<any> {
    const data = {
      username: username,
      installation: installation
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/menu`, data, { headers });
  }

  // 🚢 TRACKING JOBS METHODS
  getTrackingJobsList(userKey: number, isAdmin: boolean, head_branches: string, default_installation: string, filters: any = {}, limit: number = 50, offset: number = 0): Observable<any> {
    const data = {
      userKey: userKey,
      isAdmin: isAdmin,
      head_branches: head_branches,
      default_installation: default_installation,
      filters: filters,
      limit: limit,
      offset: offset
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/tracking/jobs/list`, data, { headers });
  }

  getTrackingJobDetail(userKey: number, shipmentId: string): Observable<any> {
    const data = {
      userKey: userKey,
      shipmentId: shipmentId
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/tracking/jobs/detail`, data, { headers });
  }

  getTrackingCustomers(userKey: number, isAdmin: boolean, head_branches: string, default_installation: string, searchQuery: string = '', limit: number = 10): Observable<any> {
    const data = {
      userKey: userKey,
      isAdmin: isAdmin,
      head_branches: head_branches,
      default_installation: default_installation,
      searchQuery: searchQuery,
      limit: limit
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/tracking/customers/list`, data, { headers });
  }

  getUserStatistics(rangesDate, options?: { userKey?: number | string; branchKey?: string }) {
    const user = this.storageService.getUserStorage();
    const resolvedUserKey = options?.userKey ?? user?.id ?? '';
    const resolvedBranchKey = options?.branchKey ?? user?.jarvis_default_installation ?? user?.default_installation ?? '';

    const data = {
      rangesDate: rangesDate,
      userKey: resolvedUserKey,
      v: new Date().getTime(),
      branchKey: resolvedBranchKey,
      criterio: 'jobs'
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/user/load/statistics`, data, { headers });
  }

  // ===== CONTACT GROUPS MANAGEMENT =====
  
  /**
   * Lista tutti i contact groups di un utente
   * @param userKey - Chiave dell'utente
   * @returns Observable con la lista dei contact groups
   */
  listContactGroups(userKey: number): Observable<any> {
    const data = { userKey: userKey };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/list/contact_groups`, data, { headers })
      .pipe(timeout(30000));
  }

  /**
   * Inserisce un nuovo contact group
   * @param description - Descrizione del gruppo
   * @param userKey - Chiave dell'utente
   * @returns Observable con il risultato dell'inserimento
   */
  insertContactGroup(description: string, userKey: number): Observable<any> {
    const data = { 
      description: description,
      userKey: userKey 
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/customers/insert/contact_group`, data, { headers })
      .pipe(timeout(30000));
  }

  // ===== CUSTOMER PROFILE =====

  /**
   * Get Customer Profile Data as JSON
   * @param customerId - Customer ID
   * @param language - Language code (it, en, es)
   * @returns Observable with profile data
   */
  getCustomerProfileData(customerId: number, language: string = 'en'): Observable<any> {
    const data = {
      customerId: customerId,
      language: language,
      type: 'json'
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiBaseUrl}customer_profile_print`, data, { headers })
      .pipe(timeout(30000));
  }

  /**
   * Get Customer Profile as PDF
   * @param customerId - Customer ID
   * @param language - Language code (it, en, es)
   * @returns Observable with PDF data (base64)
   */
  getCustomerProfilePdf(customerId: number, language: string = 'en'): Observable<any> {
    const data = {
      customerId: customerId,
      language: language,
      type: 'pdf'
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiBaseUrl}customer_profile_print`, data, { headers })
      .pipe(timeout(60000));
  }

  /**
   * @deprecated Use getCustomerProfilePdf instead
   * Generate Customer Profile PDF (legacy method for backward compatibility)
   */
  generateCustomerProfilePdf(customerId: number, language: string = 'en'): Observable<any> {
    return this.getCustomerProfilePdf(customerId, language);
  }

  // ============================================
  // 🔧 USER CRM MANAGEMENT (SuperAdmin only)
  // ============================================

  /**
   * Get CRM details for a user
   * @param targetUserKey - User ID to get details for
   */
  getUserCrmDetails(targetUserKey: number): Observable<any> {
    const data = { targetUserKey };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/crm/details`, data, { headers });
  }

  /**
   * Update CRM configuration for a user
   * @param requestingUserKey - SuperAdmin user making the request
   * @param targetUserKey - User ID to update
   * @param updateData - Data to update (enabled, userType, tma, iec)
   */
  updateUserCrmConfig(requestingUserKey: number, targetUserKey: number, updateData: {
    enabled?: boolean;
    userType?: string;
    tma?: string;
    iec?: string;
    prospectLimit?: number | null;
  }): Observable<any> {
    const data = {
      requestingUserKey,
      targetUserKey,
      ...updateData
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/crm/update`, data, { headers });
  }

  /**
   * Reset CRM password for a user
   * @param requestingUserKey - SuperAdmin user making the request
   * @param targetUserKey - User ID to reset password for
   * @param newPassword - New password (plain text)
   */
  resetUserCrmPassword(requestingUserKey: number, targetUserKey: number, newPassword: string): Observable<any> {
    const data = {
      requestingUserKey,
      targetUserKey,
      newPassword
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/crm/reset-password`, data, { headers });
  }

  /**
   * Initialize CRM for a Jarvis user
   * @param requestingUserKey - SuperAdmin user making the request
   * @param targetUserKey - User ID to initialize
   * @param crmData - Initial CRM data
   */
  initializeUserForCrm(requestingUserKey: number, targetUserKey: number, crmData: {
    password?: string;
    enabled?: boolean;
    userType?: string;
    tma?: string;
    iec?: string;
  }): Observable<any> {
    const data = {
      requestingUserKey,
      targetUserKey,
      ...crmData
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/crm/initialize`, data, { headers });
  }

  /**
   * Get installations for a user
   * @param targetUserKey - User ID
   */
  getUserInstallations(targetUserKey: number): Observable<any> {
    const data = { targetUserKey };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/installations/list`, data, { headers });
  }

  /**
   * Add installation to a user
   * @param requestingUserKey - SuperAdmin user making the request
   * @param targetUserKey - User ID
   * @param installationData - Installation data
   */
  addUserInstallation(requestingUserKey: number, targetUserKey: number, installationData: {
    code: string;
    isManager?: boolean;
    isSalesManager?: boolean;
    isDefault?: boolean;
  }): Observable<any> {
    const data = {
      requestingUserKey,
      targetUserKey,
      ...installationData
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/installations/add`, data, { headers });
  }

  /**
   * Update user installation
   * @param requestingUserKey - SuperAdmin user making the request
   * @param installationId - Installation ID to update
   * @param updateData - Data to update
   */
  updateUserInstallation(requestingUserKey: number, installationId: number, updateData: {
    isManager?: boolean;
    isSalesManager?: boolean;
    isDefault?: boolean;
  }): Observable<any> {
    const data = {
      requestingUserKey,
      installationId,
      ...updateData
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/installations/update`, data, { headers });
  }

  /**
   * Remove installation from a user
   * @param requestingUserKey - SuperAdmin user making the request
   * @param installationId - Installation ID to remove
   */
  removeUserInstallation(requestingUserKey: number, installationId: number): Observable<any> {
    const data = {
      requestingUserKey,
      installationId
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/installations/remove`, data, { headers });
  }

  /**
   * Send CRM credentials email to user
   * @param requestingUserKey - SuperAdmin user making the request
   * @param targetUserKey - User ID to send credentials to
   * @param password - Password to include in email
   * @param messageType - 'new' for new credentials, 'reset' for password reset
   */
  sendCrmCredentialsEmail(requestingUserKey: number, targetUserKey: number, password: string, messageType: 'new' | 'reset' = 'new'): Observable<any> {
    const data = {
      requestingUserKey,
      targetUserKey,
      password,
      messageType
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/crm/send-credentials`, data, { headers });
  }

  /**
   * Send CRM credentials email to multiple users (bulk)
   * Generates new passwords for each user and sends them via email
   * @param requestingUserKey - SuperAdmin user making the request
   * @param targetUserKeys - Array of user IDs to send credentials to
   */
  sendCrmCredentialsEmailBulk(requestingUserKey: number, targetUserKeys: number[]): Observable<any> {
    const data = {
      requestingUserKey,
      targetUserKeys
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/crm/send-credentials-bulk`, data, { headers });
  }

  /**
   * Generate a random password following company pattern
   */
  generateCrmPassword(): Observable<any> {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/crm/generate-password`, {}, { headers });
  }

  // ============================================
  // NOTIFICATION PREFERENCES
  // ============================================

  /**
   * Get all notification types with user preferences
   * Returns all active notification types with enabled/disabled status for the user
   * @param requestingUserKey - User making the request (must have CONFIG_NOTIFICATIONS right)
   * @param targetUserKey - User ID to get preferences for
   */
  getNotificationPrefs(requestingUserKey: number, targetUserKey: number): Observable<any> {
    const data = { requestingUserKey, targetUserKey };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/notifications/prefs`, data, { headers });
  }

  /**
   * Update notification preferences for a user
   * @param requestingUserKey - SuperAdmin user making the request
   * @param targetUserKey - User ID to update preferences for
   * @param preferences - Array of {notificationType: string, enabled: boolean}
   */
  updateNotificationPrefs(
    requestingUserKey: number,
    targetUserKey: number,
    preferences: Array<{ notificationType: string; enabled: boolean }>
  ): Observable<any> {
    const data = {
      requestingUserKey,
      targetUserKey,
      preferences
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/notifications/update`, data, { headers });
  }

  /**
   * Toggle a single notification preference
   * @param requestingUserKey - SuperAdmin user making the request
   * @param targetUserKey - User ID to update
   * @param notificationType - Type of notification to toggle
   * @param enabled - New enabled state
   */
  toggleNotificationPref(
    requestingUserKey: number,
    targetUserKey: number,
    notificationType: string,
    enabled: boolean
  ): Observable<any> {
    const data = {
      requestingUserKey,
      targetUserKey,
      notificationType,
      enabled
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/notifications/toggle`, data, { headers });
  }

  // ============================================
  // 🔐 USER RIGHTS MANAGEMENT
  // ============================================

  /**
   * Get all rights for a user (only users with canManageRights can call this)
   * @param requestingUserKey - User making the request (must have canManageRights)
   * @param targetUserKey - User ID to get rights for
   */
  getUserRights(requestingUserKey: number, targetUserKey: number): Observable<any> {
    const data = { requestingUserKey, targetUserKey };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/rights/list`, data, { headers });
  }

  /**
   * Update a user's right
   * @param requestingUserKey - User making the request (must have canManageRights)
   * @param targetUserKey - User ID to update
   * @param rightCode - Code of the right to update
   * @param enabled - New enabled state
   */
  updateUserRight(
    requestingUserKey: number,
    targetUserKey: number,
    rightCode: string,
    enabled: boolean
  ): Observable<any> {
    const data = { requestingUserKey, targetUserKey, rightCode, enabled };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiUrl}/admin/user/rights/update`, data, { headers });
  }

  /**
   * Global Search across multiple entities (customers, quotes, events, sales_requests)
   */
  globalSearch(searchQuery: string, entities: string[], pageSize: number = 5): Observable<any> {
    const user = this.storageService.getUserStorage();
    const data = {
      searchQuery: searchQuery,
      entities: entities,
      userKey: user?.id,
      isAdmin: user?.flgUserType === 'A' || user?.flgUserType === 'S',
      flgUserType: user?.flgUserType || 'U',
      default_installation: user?.default_installation,
      headBranches: user?.head_branches,
      pageSize: pageSize
    };
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.post(`${this.apiBaseUrl}crm/search/global`, data, { headers })
      .pipe(timeout(15000));
  }

  /**
   * Helper: verifica se tutte le installazioni dell'utente sono no_jobs.
   * Usato per passare il flag isAllNoJobs nelle chiamate API.
   */
  private _isAllNoJobs(): boolean {
    const user = this.storageService.getUserStorage();
    if (!user?.user_installations_config || user.user_installations_config.length === 0) {
      return false;
    }
    return user.user_installations_config.every(
      (c: any) => c.user_job_data_installation === 0
    );
  }

}
